import React, { useState } from 'react';
import styled from 'styled-components';
import { Modal } from './comps';
import { FormikProvider, useFormik } from 'formik';
import { Poppins, ScenarioModalSkeleton, Spacer } from 'src/common';
import { queryCache } from 'react-query';
import { ControllStatus, QKeys } from 'src/api/types';
import { Guard } from './Guard';
import { getDiff } from 'src/utils/getDiff';
import RmpForm from '../formComposed/RmpForm';
import { Alert } from '@mui/material';
import { rmpInitialValues, rmpValidationSchema } from '../formComposed/RmpForm/util';
import { selectUserOption } from 'src/utils/misc';
import { useAuth } from 'src/state/auth';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { isQueryLoading, KeyType, useCache } from 'src/graphql';
import { ControlInput, useControlQuery, useUpsertControlsMutation } from 'src/graphql/_generated';
import { showSuccessToast, toastMessages } from 'src/components/Toast';

const StyledModal = styled(Modal)`
  width: 1400px;
  min-height: 200px;
  padding: 0 !important;
  max-height: calc(100% - 40px);
  overflow-y: auto;

  .grid {
    display: grid;
    grid-gap: 20px;

    &--3 {
      grid-template-columns: 1fr 1fr 1fr;
    }
  }

  .helper-text-err {
    font-size: 14px;
    color: ${({ theme }) => theme.colors.error};
    margin-left: 14px;
  }
  .error {
    color: ${({ theme }) => theme.colors.error};
    font-size: 14px;
    text-align: center;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    .grid {
      grid-gap: 10px;
    }
  }
  ${({ theme }) => theme.breakpoints.down('sm')} {
    .grid {
      &--3 {
        grid-template-columns: 1fr;
      }
    }
  }
`;

const ErrorLabel = styled.div`
  margin-top: 15px;
  text-align: center;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.error};
  font-weight: 400;
`;

interface RiskManagementModalProps {
  onClose: () => void;
  controlId?: string;
  invalidateQueries: KeyType[];
}

export const RiskManagementModal: React.FC<RiskManagementModalProps> = ({ onClose, controlId, invalidateQueries }) => {
  const [responseErr, setResponseErr] = useState('');
  const [isCloseRequested, setIsCloseRequested] = useState(false);
  const { usersOptios, workspaceId } = useAuth();
  const { refetchQuery } = useCache();

  const { data, loading, networkStatus } = useControlQuery({ variables: { id: controlId! }, skip: !controlId });
  const initialValues = data?.control;
  const isInitialLive = initialValues?.status === ControllStatus.Live;

  const isLoading = isQueryLoading({ data, loading, networkStatus });

  const onError = (err: any) => setResponseErr(err.message || 'Server error');

  const [upsertControls] = useUpsertControlsMutation({ onError });

  const initValues: ControlInput = initialValues
    ? {
        strategy: initialValues.strategy,
        name: initialValues.name,
        status: initialValues.status,
        owner: initialValues.owner,
        description: initialValues.description,
        expectedActiveDate: initialValues.expectedActiveDate,
        reviewDate: initialValues.reviewDate,
        annualCost: initialValues.annualCost,
        vendor: initialValues.vendor,
        actualActiveDate: initialValues.actualActiveDate,
        origin: initialValues.origin,
        linkUrl: initialValues.linkUrl,
        linkText: initialValues.linkText,
        existingMaturity: initialValues.existingMaturity,
        targetMaturity: initialValues.targetMaturity,
        frameworkControlIds: initialValues.frameworkControls.map((el) => el.id) || [],
      }
    : rmpInitialValues;

  const formik = useFormik({
    initialValues: initValues,
    validationSchema: rmpValidationSchema,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const { userAdd, userUpdate } = selectUserOption({
        usersOptios,
        selectedUser: values.owner,
        initialUser: initValues.owner,
      });

      if (initialValues) {
        const diff = getDiff(initValues, values);

        await upsertControls({
          variables: {
            workspaceId,
            controlsData: [{ ...diff, owner: userUpdate, id: initialValues.id }],
          },
        });
      } else {
        await upsertControls({
          variables: {
            workspaceId,
            controlsData: [{ ...values, owner: userAdd }],
          },
        });
      }

      queryCache.invalidateQueries([QKeys.Audit]);
      await refetchQuery({ include: invalidateQueries });
      showSuccessToast(initialValues ? toastMessages.control.updated : toastMessages.control.created);
      onClose();
    },
  });

  return (
    <Guard
      onCancel={() => setIsCloseRequested(false)}
      onClose={onClose}
      isOpen={isCloseRequested}
      isDirty={formik.dirty}
      modalComponent={
        <StyledModal onRequestClose={() => setIsCloseRequested(true)} isOpen persistent={formik.isSubmitting}>
          <Spacer $px={30} />
          <div className="h-padding">
            <Poppins className="m-title" px={28}>
              {!!initialValues ? 'Edit' : 'Add'} Control
            </Poppins>
          </div>

          {isLoading && (
            <div className="h-padding">
              <Spacer $px={19} />
              <ScenarioModalSkeleton />
            </div>
          )}

          {data && (
            <>
              {isInitialLive ? (
                <div className="h-padding">
                  <Spacer $px={10} />
                  <Alert severity="warning">This control is already live.</Alert>
                  <Spacer $px={10} />
                </div>
              ) : (
                <Spacer $px={19} />
              )}
              <FormikProvider value={formik}>
                <RmpForm
                  buttonTitle={!!initialValues ? 'UPDATE' : 'CREATE'}
                  onSubmitCb={() =>
                    mpEvent(MPEvents.ButtonClick, {
                      button: initialValues ? 'Update' : 'Create',
                      modal: 'Risk management modal',
                      tags: ['RMP'],
                    })
                  }
                />
              </FormikProvider>
            </>
          )}
          {!!responseErr && <ErrorLabel>{responseErr}</ErrorLabel>}
          <Spacer $px={30} />
        </StyledModal>
      }
    />
  );
};
